import React, { useState, useRef, useEffect, useContext } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  LineChart, Line, Legend,
} from 'recharts';
import {
  FileText, Download, AlertTriangle, TrendingUp, TrendingDown, Minus,
  BarChart2, BarChart3, Users,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import AuthContext from '../contexts/AuthContext';
import ReportFilterBar from '../components/ReportFilterBar';
import ReportExportButtons from '../components/ReportExportButtons';

// ── helpers ────────────────────────────────────────────────────────────────

const TYP_LABELS = {
  franvaro_utan_anledning: 'Frånvaro utan anledning',
  missad_aktivitet: 'Missad aktivitet',
  regelbrott: 'Regelbrott',
  ovrigt: 'Övrigt',
};

const STATUS_LABELS = {
  oppen: 'Öppen',
  utreds: 'Utreds',
  atgardad: 'Åtgärdad',
  stangd: 'Stängd',
};

const ALLVAR_CONFIG = {
  lag: { label: 'Låg', color: '#22c55e' },
  medel: { label: 'Medel', color: '#F9CF3F' },
  hog: { label: 'Hög', color: '#ef4444' },
};

function getToday() {
  return new Date().toISOString().split('T')[0];
}

function getMonthStart() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function getDateStr(daysAgo) {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().split('T')[0];
}

function defaultFilter() {
  return { from_date: getDateStr(30), to_date: getToday() };
}

function buildParams(filter) {
  return new URLSearchParams(
    Object.fromEntries(
      Object.entries(filter).filter(([, v]) => v !== undefined && v !== null && v !== '')
    )
  ).toString();
}

function KpiCard({ label, value, sub }) {
  return (
    <Card>
      <CardContent className="p-6">
        <p className="text-sm text-muted-foreground mb-1">{label}</p>
        <p className="text-3xl font-heading font-black">{value ?? '—'}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

// ── helpers ────────────────────────────────────────────────────────────────

const GOAL_STATUS_LABELS = {
  active: 'Aktiv',
  completed: 'Slutförd',
  cancelled: 'Avbruten',
  paused: 'Pausad',
};

function goalProgress(g) {
  if (g.target_value && g.target_value > 0) {
    return Math.min(100, Math.round((g.current_value / g.target_value) * 100));
  }
  return 0;
}

// ── participants report ────────────────────────────────────────────────────

const ParticipantsReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reports/participants-summary');
      setData(res.data);
    } catch {
      toast.error('Kunde inte hämta deltagarrapport');
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = () => {
    if (!data) return;
    const rows = data.participants.map(p => ({
      Namn: p.name || '',
      Status: p.status || '',
      'PN-nummer (sista 4)': p.personal_identity_number_last4 || '',
      'GDPR-samtycke': p.gdpr_consent_given ? 'Ja' : 'Nej',
      Inskrivningsdatum: p.created_at ? p.created_at.slice(0, 10) : '',
      'Enhet/Verksamhet': p.entity_name || '',
      Avslutsdatum: p.closure_date || '',
      Avslutsskäl: p.closure_reason || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Deltagare');
    XLSX.writeFile(wb, `deltagarrapport_${getToday()}.xlsx`);
    toast.success('Excel exporterad!');
  };

  const exportPDF = () => {
    if (!data) return;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    let y = 20;

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Deltagarrapport', pageW / 2, y, { align: 'center' });
    y += 8;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Genererad: ${getToday()}`, pageW / 2, y, { align: 'center' });
    y += 12;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Sammanfattning', 14, y); y += 7;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Totalt deltagare: ${data.total}`, 14, y); y += 6;
    doc.text(`Aktiva: ${data.active}`, 14, y); y += 6;
    doc.text(`Med GDPR-samtycke: ${data.with_consent}`, 14, y); y += 12;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(`Deltagare (${data.total})`, 14, y); y += 7;
    doc.setFontSize(9);

    const cols = [14, 65, 100, 130, 155];
    const headers = ['Namn', 'Status', 'PN (sista 4)', 'Samtycke', 'Inskriven'];
    headers.forEach((h, i) => doc.text(h, cols[i], y));
    y += 2;
    doc.setLineWidth(0.3);
    doc.line(14, y, pageW - 14, y);
    y += 4;

    doc.setFont('helvetica', 'normal');
    for (const p of data.participants) {
      if (y > 270) { doc.addPage(); y = 20; }
      doc.text(String(p.name || '').slice(0, 24), cols[0], y);
      doc.text(String(p.status || '').slice(0, 18), cols[1], y);
      doc.text(String(p.personal_identity_number_last4 || '—'), cols[2], y);
      doc.text(p.gdpr_consent_given ? 'Ja' : 'Nej', cols[3], y);
      doc.text(p.created_at ? p.created_at.slice(0, 10) : '—', cols[4], y);
      y += 6;
    }

    doc.save(`deltagarrapport_${getToday()}.pdf`);
    toast.success('PDF exporterad!');
  };

  return (
    <div className="space-y-6" data-testid="participants-report">
      <div className="flex items-center gap-3">
        <FileText size={22} className="text-primary" />
        <div>
          <h2 className="text-lg font-heading font-bold">Deltagarrapport</h2>
          <p className="text-sm text-muted-foreground">Sammanställning av alla deltagare, status och samtycke</p>
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-wrap gap-4 items-center">
            <Button
              data-testid="generate-participants-btn"
              onClick={fetchReport}
              disabled={loading}
              className="rounded-full font-bold"
            >
              {loading ? 'Hämtar...' : 'Generera rapport'}
            </Button>
            {data && (
              <>
                <Button
                  variant="outline"
                  onClick={exportExcel}
                  className="rounded-full"
                  data-testid="export-participants-excel-btn"
                >
                  <Download size={16} className="mr-2" />Excel
                </Button>
                <Button
                  variant="outline"
                  onClick={exportPDF}
                  className="rounded-full"
                  data-testid="export-participants-pdf-btn"
                >
                  <Download size={16} className="mr-2" />PDF
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {!data && !loading && (
        <Card>
          <CardContent className="p-10 text-center">
            <FileText size={40} className="mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground">Klicka "Generera rapport" för att hämta data</p>
          </CardContent>
        </Card>
      )}

      {data && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <KpiCard label="Totalt deltagare" value={data.total} />
            <KpiCard label="Aktiva" value={data.active} />
            <KpiCard label="Med GDPR-samtycke" value={data.with_consent} />
          </div>

          <Card>
            <CardHeader className="border-b pb-3">
              <CardTitle className="font-heading font-bold text-base">
                Alla deltagare ({data.total})
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 overflow-x-auto">
              {data.participants.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4 text-center">Inga deltagare.</p>
              ) : (
                <table className="w-full text-sm" data-testid="participants-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 font-semibold">Namn</th>
                      <th className="text-left py-2 font-semibold">Status</th>
                      <th className="text-left py-2 font-semibold">PN (sista 4)</th>
                      <th className="text-left py-2 font-semibold">GDPR-samtycke</th>
                      <th className="text-left py-2 font-semibold">Inskrivningsdatum</th>
                      <th className="text-left py-2 font-semibold">Enhet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.participants.map(p => (
                      <tr key={p.id} className="border-b last:border-0 hover:bg-muted/40">
                        <td className="py-2 font-medium">{p.name}</td>
                        <td className="py-2">{p.status}</td>
                        <td className="py-2 font-mono text-muted-foreground">
                          {p.personal_identity_number_last4 ? `••••••-${p.personal_identity_number_last4}` : '—'}
                        </td>
                        <td className="py-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                            p.gdpr_consent_given ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {p.gdpr_consent_given ? 'Ja' : 'Nej'}
                          </span>
                        </td>
                        <td className="py-2 text-muted-foreground">
                          {p.created_at ? p.created_at.slice(0, 10) : '—'}
                        </td>
                        <td className="py-2 text-muted-foreground">{p.entity_name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

// ── goals report ───────────────────────────────────────────────────────────

const GoalsReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reports/goals-summary');
      setData(res.data);
    } catch {
      toast.error('Kunde inte hämta målrapport');
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = () => {
    if (!data) return;
    const rows = data.goals.map(g => ({
      Mål: g.title || '',
      Status: GOAL_STATUS_LABELS[g.status] || g.status || '',
      'Framsteg (%)': goalProgress(g),
      'Nuvarande värde': g.current_value ?? '',
      'Målvärde': g.target_value ?? '',
      Enhet: g.unit || '',
      Startdatum: g.created_at ? g.created_at.slice(0, 10) : '',
      Måldatum: g.due_date ? g.due_date.slice(0, 10) : '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Mål');
    XLSX.writeFile(wb, `malrapport_${getToday()}.xlsx`);
    toast.success('Excel exporterad!');
  };

  const exportPDF = () => {
    if (!data) return;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    let y = 20;

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Malrapport', pageW / 2, y, { align: 'center' });
    y += 8;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Genererad: ${getToday()}`, pageW / 2, y, { align: 'center' });
    y += 12;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Sammanfattning', 14, y); y += 7;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Totalt mal: ${data.total}`, 14, y); y += 6;
    doc.text(`Aktiva: ${data.active}`, 14, y); y += 6;
    doc.text(`Slutforda: ${data.completed}`, 14, y); y += 12;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(`Mal (${data.total})`, 14, y); y += 7;
    doc.setFontSize(9);

    const cols = [14, 80, 115, 145, 170];
    const headers = ['Mal', 'Status', 'Framsteg', 'Malvarde', 'Maldatum'];
    headers.forEach((h, i) => doc.text(h, cols[i], y));
    y += 2;
    doc.setLineWidth(0.3);
    doc.line(14, y, pageW - 14, y);
    y += 4;

    doc.setFont('helvetica', 'normal');
    for (const g of data.goals) {
      if (y > 270) { doc.addPage(); y = 20; }
      doc.text(String(g.title || '').slice(0, 30), cols[0], y);
      doc.text(String(GOAL_STATUS_LABELS[g.status] || g.status || ''), cols[1], y);
      doc.text(`${goalProgress(g)}%`, cols[2], y);
      doc.text(g.target_value != null ? String(g.target_value) : '—', cols[3], y);
      doc.text(g.due_date ? g.due_date.slice(0, 10) : '—', cols[4], y);
      y += 6;
    }

    doc.save(`malrapport_${getToday()}.pdf`);
    toast.success('PDF exporterad!');
  };

  return (
    <div className="space-y-6" data-testid="goals-report">
      <div className="flex items-center gap-3">
        <FileText size={22} className="text-primary" />
        <div>
          <h2 className="text-lg font-heading font-bold">Målrapport</h2>
          <p className="text-sm text-muted-foreground">Sammanfattning av mål, framsteg och slutförande</p>
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-wrap gap-4 items-center">
            <Button
              data-testid="generate-goals-btn"
              onClick={fetchReport}
              disabled={loading}
              className="rounded-full font-bold"
            >
              {loading ? 'Hämtar...' : 'Generera rapport'}
            </Button>
            {data && (
              <>
                <Button
                  variant="outline"
                  onClick={exportExcel}
                  className="rounded-full"
                  data-testid="export-goals-excel-btn"
                >
                  <Download size={16} className="mr-2" />Excel
                </Button>
                <Button
                  variant="outline"
                  onClick={exportPDF}
                  className="rounded-full"
                  data-testid="export-goals-pdf-btn"
                >
                  <Download size={16} className="mr-2" />PDF
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {!data && !loading && (
        <Card>
          <CardContent className="p-10 text-center">
            <FileText size={40} className="mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground">Klicka "Generera rapport" för att hämta data</p>
          </CardContent>
        </Card>
      )}

      {data && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <KpiCard label="Totalt mål" value={data.total} />
            <KpiCard label="Aktiva mål" value={data.active} />
            <KpiCard label="Slutförda mål" value={data.completed} />
          </div>

          <Card>
            <CardHeader className="border-b pb-3">
              <CardTitle className="font-heading font-bold text-base">
                Alla mål ({data.total})
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 overflow-x-auto">
              {data.goals.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4 text-center">Inga mål.</p>
              ) : (
                <table className="w-full text-sm" data-testid="goals-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 font-semibold">Mål</th>
                      <th className="text-left py-2 font-semibold">Status</th>
                      <th className="text-right py-2 font-semibold">Framsteg</th>
                      <th className="text-left py-2 font-semibold">Enhet</th>
                      <th className="text-left py-2 font-semibold">Måldatum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.goals.map(g => {
                      const pct = goalProgress(g);
                      return (
                        <tr key={g.id} className="border-b last:border-0 hover:bg-muted/40">
                          <td className="py-2 font-medium">{g.title}</td>
                          <td className="py-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                              g.status === 'completed' ? 'bg-green-100 text-green-700'
                              : g.status === 'cancelled' ? 'bg-red-100 text-red-700'
                              : 'bg-blue-100 text-blue-700'
                            }`}>
                              {GOAL_STATUS_LABELS[g.status] || g.status}
                            </span>
                          </td>
                          <td className="py-2 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-16 h-1.5 rounded-full bg-muted overflow-hidden">
                                <div
                                  className="h-full rounded-full"
                                  style={{ width: `${pct}%`, background: pct >= 100 ? '#22c55e' : '#F9CF3F' }}
                                />
                              </div>
                              <span className="text-xs font-bold w-8">{pct}%</span>
                            </div>
                          </td>
                          <td className="py-2 text-muted-foreground">{g.unit || '—'}</td>
                          <td className="py-2 text-muted-foreground">
                            {g.due_date ? g.due_date.slice(0, 10) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

// ── existing reports (Deltagarrapport-fliken) ──────────────────────────────

const ExistingReports = () => (
  <div className="space-y-10">
    <ParticipantsReport />
    <GoalsReport />
  </div>
);

// ── ORG-1 Översikt ─────────────────────────────────────────────────────────

const OverviewReport = ({ role, participants, handlers }) => {
  const [filter, setFilter] = useState(defaultFilter());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/reports/overview?${buildParams(filter)}`);
      setData(res.data);
    } catch {
      toast.error('Kunde inte hämta verksamhetsöversikt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="overview-report">
      <ReportFilterBar
        role={role}
        participants={participants}
        handlers={handlers}
        onFilterChange={setFilter}
      />

      <div className="flex items-center justify-between flex-wrap gap-3">
        <Button
          onClick={fetchReport}
          disabled={loading}
          className="rounded-full font-bold"
          data-testid="generate-overview-btn"
        >
          {loading ? 'Hämtar...' : 'Generera rapport'}
        </Button>
        <ReportExportButtons
          data={data}
          reportType="overview"
          period={{ from: filter.from_date, to: filter.to_date }}
          role={role}
        />
      </div>

      {!data && !loading && (
        <Card>
          <CardContent className="p-10 text-center">
            <BarChart3 size={40} className="mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground">Klicka "Generera rapport" för att hämta data</p>
          </CardContent>
        </Card>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard label="Aktiva deltagare" value={data.total_active_participants} />
            <KpiCard
              label="Inskrivna / Avslutade"
              value={`${data.enrolled_in_period ?? 0} / ${data.closed_in_period ?? 0}`}
            />
            <KpiCard
              label="Närvaro"
              value={data.attendance_rate != null ? `${data.attendance_rate}%` : '—'}
            />
            <KpiCard label="Aktiva mål" value={data.active_goals} />
          </div>

          <Card>
            <CardHeader className="border-b pb-3">
              <CardTitle className="font-heading font-bold text-base">Alla nyckeltal</CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <table className="w-full text-sm" data-testid="overview-kpi-table">
                <tbody>
                  {[
                    ['Aktiva deltagare', data.total_active_participants],
                    ['Inskrivna under perioden', data.enrolled_in_period],
                    ['Avslutade under perioden', data.closed_in_period],
                    ['Närvaro (%)', data.attendance_rate != null ? `${data.attendance_rate}%` : '—'],
                    ['Aktiva mål', data.active_goals],
                    ['Uppnådda mål', data.achieved_goals],
                    ['Öppna avvikelser', data.open_deviations],
                  ].map(([lbl, val]) => (
                    <tr key={lbl} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="py-2 text-slate-600">{lbl}</td>
                      <td className="py-2 text-right font-bold text-slate-800">{val ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

// ── ORG-2 Närvaro ──────────────────────────────────────────────────────────

const AttendanceTrendReport = ({ role, participants, handlers }) => {
  const [filter, setFilter] = useState(defaultFilter());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/reports/attendance-trend?${buildParams(filter)}`);
      setData(res.data);
    } catch {
      toast.error('Kunde inte hämta närvarodata');
    } finally {
      setLoading(false);
    }
  };

  const chartData = data
    ? data.trend.map(t => ({
        period: t.period,
        'Närvaro (%)': t.attendance_rate,
      }))
    : [];

  return (
    <div className="space-y-5" data-testid="attendance-trend-report">
      <ReportFilterBar
        role={role}
        participants={participants}
        handlers={handlers}
        onFilterChange={setFilter}
      />

      <div className="flex items-center justify-between flex-wrap gap-3">
        <Button
          onClick={fetchReport}
          disabled={loading}
          className="rounded-full font-bold"
          data-testid="generate-attendance-btn"
        >
          {loading ? 'Hämtar...' : 'Generera rapport'}
        </Button>
        <ReportExportButtons
          data={data}
          reportType="attendance-trend"
          period={{ from: filter.from_date, to: filter.to_date }}
          role={role}
        />
      </div>

      {!data && !loading && (
        <Card>
          <CardContent className="p-10 text-center">
            <BarChart2 size={40} className="mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground">Klicka "Generera rapport" för att hämta data</p>
          </CardContent>
        </Card>
      )}

      {data && (
        <>
          {chartData.length > 0 && (
            <Card>
              <CardHeader className="border-b pb-3">
                <CardTitle className="font-heading font-bold text-base">
                  Närvaro per {data.group_by === 'week' ? 'vecka' : 'månad'}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <ResponsiveContainer width="100%" height={240} data-testid="attendance-chart">
                  <LineChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                    <Tooltip formatter={v => [`${v}%`, 'Närvaro']} />
                    <Line
                      type="monotone"
                      dataKey="Närvaro (%)"
                      stroke="#F9CF3F"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Low attendance table */}
          <Card>
            <CardHeader className="border-b pb-3">
              <CardTitle className="font-heading font-bold text-base">
                Deltagare med låg närvaro (&lt;60%)
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              {!data.low_attendance_participants || data.low_attendance_participants.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4 text-center">
                  Alla deltagare har god närvaro
                </p>
              ) : (
                <table className="w-full text-sm" data-testid="low-attendance-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 font-semibold">Namn</th>
                      <th className="text-right py-2 font-semibold">Närvaro (%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.low_attendance_participants.map(p => (
                      <tr key={p.id} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="py-2 font-medium text-slate-800">{p.name}</td>
                        <td className="py-2 text-right font-bold text-red-600">{p.rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

// ── ORG-3 Mål ──────────────────────────────────────────────────────────────

const GoalSummaryReport = ({ role, participants, handlers }) => {
  const [filter, setFilter] = useState(defaultFilter());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/reports/goal-summary?${buildParams(filter)}`);
      setData(res.data);
    } catch {
      toast.error('Kunde inte hämta målsammanfattning');
    } finally {
      setLoading(false);
    }
  };

  const statusChartData = data
    ? [
        { name: 'Aktiva',       value: data.by_status?.active || 0,       fill: '#3b82f6' },
        { name: 'Uppnådda',     value: data.by_status?.completed || 0,    fill: '#22c55e' },
        { name: 'Ej påbörjade', value: data.by_status?.not_started || 0,  fill: '#94a3b8' },
      ]
    : [];

  return (
    <div className="space-y-5" data-testid="goal-summary-report">
      <ReportFilterBar
        role={role}
        participants={participants}
        handlers={handlers}
        onFilterChange={setFilter}
      />

      <div className="flex items-center justify-between flex-wrap gap-3">
        <Button
          onClick={fetchReport}
          disabled={loading}
          className="rounded-full font-bold"
          data-testid="generate-goal-summary-btn"
        >
          {loading ? 'Hämtar...' : 'Generera rapport'}
        </Button>
        <ReportExportButtons
          data={data}
          reportType="goal-summary"
          period={{ from: filter.from_date, to: filter.to_date }}
          role={role}
        />
      </div>

      {!data && !loading && (
        <Card>
          <CardContent className="p-10 text-center">
            <BarChart2 size={40} className="mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground">Klicka "Generera rapport" för att hämta data</p>
          </CardContent>
        </Card>
      )}

      {data && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <KpiCard label="Aktiva mål" value={data.by_status?.active} />
            <KpiCard label="Uppnådda mål" value={data.by_status?.completed} />
            <KpiCard label="Ej påbörjade" value={data.by_status?.not_started} />
          </div>

          {data.avg_days_to_completion != null && (
            <p className="text-sm text-slate-500">
              Snitt tid till uppnått mål:{' '}
              <span className="font-bold text-slate-800">{data.avg_days_to_completion} dagar</span>
            </p>
          )}

          {statusChartData.some(d => d.value > 0) && (
            <Card>
              <CardHeader className="border-b pb-3">
                <CardTitle className="font-heading font-bold text-base">Mål per status</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <ResponsiveContainer width="100%" height={200} data-testid="goal-status-chart">
                  <BarChart data={statusChartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="value" name="Antal" radius={[4, 4, 0, 0]}>
                      {statusChartData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {data.per_participant && data.per_participant.length > 0 && (
            <Card>
              <CardHeader className="border-b pb-3">
                <CardTitle className="font-heading font-bold text-base">Per deltagare</CardTitle>
              </CardHeader>
              <CardContent className="pt-4 overflow-x-auto">
                <table className="w-full text-sm" data-testid="goal-per-participant-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 font-semibold">Deltagare</th>
                      <th className="text-right py-2 font-semibold">Aktiva</th>
                      <th className="text-right py-2 font-semibold">Uppnådda</th>
                      <th className="text-right py-2 font-semibold">Totalt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.per_participant.map(p => (
                      <tr key={p.participant_id} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="py-2 font-medium text-slate-800">{p.participant_name}</td>
                        <td className="py-2 text-right">{p.active}</td>
                        <td className="py-2 text-right text-green-700 font-semibold">{p.completed}</td>
                        <td className="py-2 text-right text-slate-500">{p.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

// ── deviations report tab ──────────────────────────────────────────────────

const DeviationsReport = () => {
  const [fromDate, setFromDate] = useState(getMonthStart());
  const [toDate, setToDate] = useState(getToday());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const reportRef = useRef(null);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ from_date: fromDate, to_date: toDate });
      const res = await api.get(`/reports/deviations?${params}`);
      setData(res.data);
    } catch {
      toast.error('Kunde inte hämta avvikelserapport');
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = () => {
    if (!data) return;
    const rows = data.items.map(d => ({
      Deltagare: d.participant_name,
      Typ: TYP_LABELS[d.typ] || d.typ,
      Allvarlighetsgrad: ALLVAR_CONFIG[d.allvarlighetsgrad]?.label || d.allvarlighetsgrad,
      Status: STATUS_LABELS[d.status] || d.status,
      Datum: d.rapporterad_datum ? d.rapporterad_datum.slice(0, 10) : '',
      'Åtgärdstid (dagar)': d.resolution_days ?? '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Avvikelser');
    XLSX.writeFile(wb, `avvikelserapport_${fromDate}_${toDate}.xlsx`);
    toast.success('Excel exporterad!');
  };

  const exportPDF = () => {
    if (!data) return;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    let y = 20;

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Avvikelserapport', pageW / 2, y, { align: 'center' });
    y += 8;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Period: ${fromDate} — ${toDate}`, pageW / 2, y, { align: 'center' });
    y += 12;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Sammanfattning', 14, y); y += 7;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Totalt avvikelser: ${data.total}`, 14, y); y += 6;
    doc.text(`Öppna avvikelser: ${data.open}`, 14, y); y += 6;
    doc.text(`Åtgärdade: ${data.resolved}`, 14, y); y += 6;
    doc.text(
      `Genomsnittlig åtgärdstid: ${data.avg_resolution_days != null ? data.avg_resolution_days + ' dagar' : '—'}`,
      14, y
    ); y += 6;
    const resolvedPct = data.total > 0 ? Math.round((data.resolved / data.total) * 100) : 0;
    doc.text(`Andel åtgärdade: ${resolvedPct}%`, 14, y); y += 12;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Avvikelser', 14, y); y += 7;
    doc.setFontSize(9);

    const cols = [14, 60, 100, 130, 155, 180];
    const headers = ['Deltagare', 'Typ', 'Allvarlighet', 'Status', 'Datum', 'Dagar'];
    headers.forEach((h, i) => doc.text(h, cols[i], y));
    y += 2;
    doc.setLineWidth(0.3);
    doc.line(14, y, pageW - 14, y);
    y += 4;

    doc.setFont('helvetica', 'normal');
    for (const item of data.items) {
      if (y > 270) { doc.addPage(); y = 20; }
      doc.text(String(item.participant_name || '').slice(0, 22), cols[0], y);
      doc.text(String(TYP_LABELS[item.typ] || item.typ).slice(0, 20), cols[1], y);
      doc.text(String(ALLVAR_CONFIG[item.allvarlighetsgrad]?.label || item.allvarlighetsgrad), cols[2], y);
      doc.text(String(STATUS_LABELS[item.status] || item.status), cols[3], y);
      doc.text(item.rapporterad_datum ? item.rapporterad_datum.slice(0, 10) : '', cols[4], y);
      doc.text(item.resolution_days != null ? String(item.resolution_days) : '—', cols[5], y);
      y += 6;
    }

    doc.save(`avvikelserapport_${fromDate}_${toDate}.pdf`);
    toast.success('PDF exporterad!');
  };

  const typChartData = data
    ? Object.entries(data.by_typ).map(([k, v]) => ({ name: TYP_LABELS[k] || k, value: v }))
    : [];
  const allvarChartData = data
    ? ['lag', 'medel', 'hog'].map(k => ({
        name: ALLVAR_CONFIG[k].label,
        value: data.by_allvarlighetsgrad[k] || 0,
        color: ALLVAR_CONFIG[k].color,
      }))
    : [];

  const resolvedPct = data && data.total > 0
    ? Math.round((data.resolved / data.total) * 100)
    : 0;

  return (
    <div className="space-y-6" ref={reportRef}>
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-1">
              <Label>Från</Label>
              <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-40" />
            </div>
            <div className="space-y-1">
              <Label>Till</Label>
              <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-40" />
            </div>
            <Button onClick={fetchReport} disabled={loading} className="rounded-full font-bold">
              {loading ? 'Hämtar...' : 'Generera rapport'}
            </Button>
            {data && (
              <>
                <Button variant="outline" onClick={exportExcel} className="rounded-full">
                  <Download size={16} className="mr-2" />Excel
                </Button>
                <Button variant="outline" onClick={exportPDF} className="rounded-full">
                  <Download size={16} className="mr-2" />PDF
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {!data && !loading && (
        <Card>
          <CardContent className="p-12 text-center">
            <AlertTriangle size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground text-lg">Välj period och klicka "Generera rapport"</p>
          </CardContent>
        </Card>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard label="Totalt avvikelser" value={data.total} />
            <KpiCard label="Öppna avvikelser" value={data.open} />
            <KpiCard
              label="Genomsnittlig åtgärdstid"
              value={data.avg_resolution_days != null ? `${data.avg_resolution_days} dagar` : '—'}
            />
            <KpiCard
              label="Andel åtgärdade"
              value={`${resolvedPct}%`}
              sub={`${data.resolved} av ${data.total}`}
            />
          </div>

          {(typChartData.length > 0 || allvarChartData.some(d => d.value > 0)) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {typChartData.length > 0 && (
                <Card>
                  <CardHeader className="border-b pb-3">
                    <CardTitle className="font-heading font-bold text-base">Fördelning per typ</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={typChartData} margin={{ top: 4, right: 8, left: 0, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" interval={0} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="value" name="Antal" fill="#F9CF3F" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader className="border-b pb-3">
                  <CardTitle className="font-heading font-bold text-base">Fördelning per allvarlighetsgrad</CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={allvarChartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="value" name="Antal" radius={[4, 4, 0, 0]}>
                        {allvarChartData.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          )}

          {data.top_participants.length > 0 && (
            <Card>
              <CardHeader className="border-b pb-3">
                <CardTitle className="font-heading font-bold text-base">Top 10 deltagare med flest avvikelser</CardTitle>
              </CardHeader>
              <CardContent className="pt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 font-semibold">#</th>
                      <th className="text-left py-2 font-semibold">Deltagare</th>
                      <th className="text-right py-2 font-semibold">Antal avvikelser</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_participants.map((p, i) => (
                      <tr key={p.participant_id} className="border-b last:border-0 hover:bg-muted/40">
                        <td className="py-2 text-muted-foreground">{i + 1}</td>
                        <td className="py-2 font-medium">{p.name}</td>
                        <td className="py-2 text-right font-bold">{p.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="border-b pb-3">
              <CardTitle className="font-heading font-bold text-base">
                Alla avvikelser ({data.total})
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 overflow-x-auto">
              {data.items.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4 text-center">Inga avvikelser för vald period.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 font-semibold">Deltagare</th>
                      <th className="text-left py-2 font-semibold">Typ</th>
                      <th className="text-left py-2 font-semibold">Allvarlighet</th>
                      <th className="text-left py-2 font-semibold">Status</th>
                      <th className="text-left py-2 font-semibold">Datum</th>
                      <th className="text-right py-2 font-semibold">Åtgärdstid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map(item => (
                      <tr key={item.id} className="border-b last:border-0 hover:bg-muted/40">
                        <td className="py-2 font-medium">{item.participant_name}</td>
                        <td className="py-2 text-muted-foreground">{TYP_LABELS[item.typ] || item.typ}</td>
                        <td className="py-2">
                          <span className="inline-flex items-center gap-1">
                            <span
                              className="w-2 h-2 rounded-full inline-block"
                              style={{ background: ALLVAR_CONFIG[item.allvarlighetsgrad]?.color || '#999' }}
                            />
                            {ALLVAR_CONFIG[item.allvarlighetsgrad]?.label || item.allvarlighetsgrad}
                          </span>
                        </td>
                        <td className="py-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                            item.status === 'atgardad' || item.status === 'stangd'
                              ? 'bg-green-100 text-green-700'
                              : item.status === 'utreds'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {STATUS_LABELS[item.status] || item.status}
                          </span>
                        </td>
                        <td className="py-2 text-muted-foreground">
                          {item.rapporterad_datum ? item.rapporterad_datum.slice(0, 10) : '—'}
                        </td>
                        <td className="py-2 text-right">
                          {item.resolution_days != null ? `${item.resolution_days} d` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

// ── helpers (shared) ──────────────────────────────────────────────────────

function attendanceRateColor(rate) {
  if (rate == null) return '';
  if (rate < 60) return 'text-red-600 font-bold';
  if (rate >= 80) return 'text-green-600 font-bold';
  return 'text-yellow-600 font-semibold';
}

// ── ORG-5 Handläggaröversikt ───────────────────────────────────────────────

const HandlerOverviewReport = ({ role, participants, handlers }) => {
  const [filter, setFilter] = useState(defaultFilter());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState('');
  const [sortDir, setSortDir] = useState('asc');

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/reports/handler-overview?${buildParams(filter)}`);
      setData(res.data);
    } catch {
      toast.error('Kunde inte hämta handläggaröversikt');
    } finally {
      setLoading(false);
    }
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sortedHandlers = data?.handlers ? [...data.handlers].sort((a, b) => {
    if (!sortKey) return 0;
    const av = a[sortKey] ?? 0;
    const bv = b[sortKey] ?? 0;
    if (av === bv) return 0;
    return (sortDir === 'asc' ? av > bv : av < bv) ? 1 : -1;
  }) : [];

  const SortTh = ({ label, field, right }) => (
    <th
      className={`py-2 font-semibold cursor-pointer select-none hover:text-slate-900 whitespace-nowrap ${right ? 'text-right' : 'text-left'}`}
      onClick={() => toggleSort(field)}
    >
      {label}{sortKey === field ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
    </th>
  );

  return (
    <div className="space-y-5" data-testid="handler-overview-report">
      <ReportFilterBar role={role} participants={participants} handlers={[]} onFilterChange={setFilter} />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Button onClick={fetchReport} disabled={loading} className="rounded-full font-bold" data-testid="generate-handler-overview-btn">
          {loading ? 'Hämtar...' : 'Generera rapport'}
        </Button>
        <ReportExportButtons data={data} reportType="handler-overview" period={{ from: filter.from_date, to: filter.to_date }} role={role} />
      </div>
      {!data && !loading && (
        <Card><CardContent className="p-10 text-center">
          <Users size={40} className="mx-auto mb-3 text-muted-foreground" />
          <p className="text-muted-foreground">Klicka "Generera rapport" för att hämta data</p>
        </CardContent></Card>
      )}
      {data && (
        <Card>
          <CardHeader className="border-b pb-3">
            <CardTitle className="font-heading font-bold text-base">Handläggare ({data.handlers?.length ?? 0})</CardTitle>
          </CardHeader>
          <CardContent className="pt-4 overflow-x-auto">
            {!data.handlers?.length ? (
              <p className="text-muted-foreground text-sm py-4 text-center">Inga handläggare hittades.</p>
            ) : (
              <table className="w-full text-sm" data-testid="handler-overview-table">
                <thead>
                  <tr className="border-b">
                    <SortTh label="Handläggare" field="name" />
                    <th className="text-left py-2 font-semibold">E-post</th>
                    <SortTh label="Aktiva deltagare" field="active_participants" right />
                    <SortTh label="Möten (30d)" field="meetings_in_period" right />
                    <SortTh label="Öppna avvikelser" field="open_deviations" right />
                    <SortTh label="Snitt-närvaro" field="avg_attendance_rate" right />
                  </tr>
                </thead>
                <tbody>
                  {sortedHandlers.map(h => (
                    <tr key={h.user_id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="py-2 font-medium text-slate-800">{h.name}</td>
                      <td className="py-2 text-slate-500 text-xs">{h.email}</td>
                      <td className="py-2 text-right">{h.active_participants}</td>
                      <td className="py-2 text-right">{h.meetings_in_period}</td>
                      <td className="py-2 text-right">{h.open_deviations}</td>
                      <td className={`py-2 text-right ${attendanceRateColor(h.avg_attendance_rate)}`}>
                        {h.avg_attendance_rate != null ? `${h.avg_attendance_rate}%` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ── ORG-6 Deltagarflöde ────────────────────────────────────────────────────

const ParticipantFlowReport = ({ role, participants, handlers }) => {
  const [filter, setFilter] = useState(defaultFilter());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/reports/participant-flow?${buildParams(filter)}`);
      setData(res.data);
    } catch {
      toast.error('Kunde inte hämta deltagarflöde');
    } finally {
      setLoading(false);
    }
  };

  const chartData = data?.flow?.map(f => ({ period: f.period, Inskrivna: f.enrolled, Avslutade: f.closed })) ?? [];
  const closureRows = data?.closure_reasons ? Object.entries(data.closure_reasons).sort((a, b) => b[1] - a[1]) : [];
  const totalClosed = closureRows.reduce((s, [, v]) => s + v, 0);
  const topClosure = closureRows[0];
  const totalNet = data?.flow?.reduce((s, f) => s + f.net, 0) ?? 0;

  return (
    <div className="space-y-5" data-testid="participant-flow-report">
      <ReportFilterBar role={role} participants={participants} handlers={handlers} onFilterChange={setFilter} />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Button onClick={fetchReport} disabled={loading} className="rounded-full font-bold" data-testid="generate-participant-flow-btn">
          {loading ? 'Hämtar...' : 'Generera rapport'}
        </Button>
        <ReportExportButtons data={data} reportType="participant-flow" period={{ from: filter.from_date, to: filter.to_date }} role={role} />
      </div>
      {!data && !loading && (
        <Card><CardContent className="p-10 text-center">
          <BarChart3 size={40} className="mx-auto mb-3 text-muted-foreground" />
          <p className="text-muted-foreground">Klicka "Generera rapport" för att hämta data</p>
        </CardContent></Card>
      )}
      {data && (
        <>
          {chartData.length > 0 && (
            <Card>
              <CardHeader className="border-b pb-3">
                <CardTitle className="font-heading font-bold text-base">Inskrivna vs avslutade per månad</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div data-testid="participant-flow-chart">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Inskrivna" fill="#22c55e" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Avslutade" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
          <div className="grid grid-cols-3 gap-4">
            <KpiCard label="Snitt-tid i program" value={data.avg_days_in_program != null ? `${data.avg_days_in_program} dagar` : '—'} />
            <KpiCard label="Vanligaste avslutningsorsak" value={topClosure ? topClosure[0] : '—'} />
            <KpiCard label="Nettoinflöde totalt" value={totalNet >= 0 ? `+${totalNet}` : String(totalNet)} />
          </div>
          {closureRows.length > 0 && (
            <Card>
              <CardHeader className="border-b pb-3">
                <CardTitle className="font-heading font-bold text-base">Avslutningsorsaker</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <table className="w-full text-sm" data-testid="closure-reasons-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 font-semibold">Orsak</th>
                      <th className="text-right py-2 font-semibold">Antal</th>
                      <th className="text-right py-2 font-semibold">Andel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {closureRows.map(([reason, count]) => (
                      <tr key={reason} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="py-2 text-slate-700">{reason}</td>
                        <td className="py-2 text-right font-bold">{count}</td>
                        <td className="py-2 text-right text-slate-500">
                          {totalClosed > 0 ? `${Math.round((count / totalClosed) * 100)}%` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

// ── UPP-1 Resultatrapport ─────────────────────────────────────────────────

const ResultSummaryReport = ({ role, participants, handlers }) => {
  const [filter, setFilter] = useState(defaultFilter());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/reports/result-summary?${buildParams(filter)}`);
      setData(res.data);
    } catch {
      toast.error('Kunde inte hämta resultatrapport');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="result-summary-report">
      <ReportFilterBar role={role} participants={participants} handlers={handlers} onFilterChange={setFilter} />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Button onClick={fetchReport} disabled={loading} className="rounded-full font-bold" data-testid="generate-result-summary-btn">
          {loading ? 'Hämtar...' : 'Generera rapport'}
        </Button>
        <ReportExportButtons data={data} reportType="result-summary" period={{ from: filter.from_date, to: filter.to_date }} role={role} />
      </div>
      {!data && !loading && (
        <Card><CardContent className="p-10 text-center">
          <TrendingUp size={40} className="mx-auto mb-3 text-muted-foreground" />
          <p className="text-muted-foreground">Klicka "Generera rapport" för att hämta data</p>
        </CardContent></Card>
      )}
      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="result-kpi-cards">
            <KpiCard label="Antal deltagare" value={data.participant_count} />
            <KpiCard label="Snitt-närvaro" value={data.attendance_rate != null ? `${data.attendance_rate}%` : '—'} />
            <KpiCard label="Mål uppnådda" value={data.goals_achieved} />
            <KpiCard label="Måluppfyllelsegrad" value={data.goals_achievement_rate != null ? `${data.goals_achievement_rate}%` : '—'} />
          </div>
          <Card>
            <CardContent className="p-5">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Avvikelser</p>
              <div className="flex gap-6">
                <span className="text-sm text-slate-600">Totalt: <span className="font-bold text-slate-800">{data.deviations_total ?? '—'}</span></span>
                <span className="text-sm text-slate-600">Öppna: <span className="font-bold text-red-600">{data.deviations_open ?? '—'}</span></span>
              </div>
            </CardContent>
          </Card>
          {data.handler_info?.length > 0 && (
            <Card>
              <CardHeader className="border-b pb-3">
                <CardTitle className="font-heading font-bold text-base">Involverade handläggare</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <ul className="space-y-2" data-testid="handler-info-list">
                  {data.handler_info.map((h, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm">
                      <span className="font-medium text-slate-800">{h.name}</span>
                      <span className="text-slate-500">{h.email}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

// ── UPP-2 Närvarorapport ──────────────────────────────────────────────────

const AttendanceReport = ({ role, participants, handlers }) => {
  const [filter, setFilter] = useState(defaultFilter());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState('attendance_rate');
  const [sortDir, setSortDir] = useState('asc');

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/reports/attendance-report?${buildParams(filter)}`);
      setData(res.data);
    } catch {
      toast.error('Kunde inte hämta närvarorapport');
    } finally {
      setLoading(false);
    }
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sortedRows = data?.participants ? [...data.participants].sort((a, b) => {
    const av = a[sortKey] ?? 0;
    const bv = b[sortKey] ?? 0;
    if (av === bv) return 0;
    return (sortDir === 'asc' ? av > bv : av < bv) ? 1 : -1;
  }) : [];

  const SortTh = ({ label, field, right }) => (
    <th
      className={`py-2 font-semibold cursor-pointer select-none hover:text-slate-900 whitespace-nowrap ${right ? 'text-right' : 'text-left'}`}
      onClick={() => toggleSort(field)}
    >
      {label}{sortKey === field ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
    </th>
  );

  return (
    <div className="space-y-5" data-testid="attendance-report">
      <ReportFilterBar role={role} participants={participants} handlers={handlers} onFilterChange={setFilter} />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Button onClick={fetchReport} disabled={loading} className="rounded-full font-bold" data-testid="generate-attendance-report-btn">
          {loading ? 'Hämtar...' : 'Generera rapport'}
        </Button>
        <ReportExportButtons data={data} reportType="attendance-report" period={{ from: filter.from_date, to: filter.to_date }} role={role} />
      </div>
      {!data && !loading && (
        <Card><CardContent className="p-10 text-center">
          <BarChart2 size={40} className="mx-auto mb-3 text-muted-foreground" />
          <p className="text-muted-foreground">Klicka "Generera rapport" för att hämta data</p>
        </CardContent></Card>
      )}
      {data && (
        <>
          {data.period_summary && (
            <div className="flex flex-wrap gap-6 p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid="period-summary-stats">
              <span className="text-sm text-slate-600">Snitt-närvaro: <span className={`font-bold ${attendanceRateColor(data.period_summary.avg_rate)}`}>{data.period_summary.avg_rate != null ? `${data.period_summary.avg_rate}%` : '—'}</span></span>
              <span className="text-sm text-slate-600">Totalt närvarande: <span className="font-bold text-slate-800">{data.period_summary.total_present ?? '—'}</span></span>
              <span className="text-sm text-slate-600">Totalt frånvaro: <span className="font-bold text-slate-800">{data.period_summary.total_absent ?? '—'}</span></span>
            </div>
          )}
          <Card>
            <CardContent className="pt-4 overflow-x-auto">
              {!sortedRows.length ? (
                <p className="text-muted-foreground text-sm py-4 text-center">Inga deltagare hittades.</p>
              ) : (
                <table className="w-full text-sm" data-testid="attendance-report-table">
                  <thead>
                    <tr className="border-b">
                      <SortTh label="Deltagare" field="participant_name" />
                      <SortTh label="Närvaro (%)" field="attendance_rate" right />
                      <SortTh label="Närv. dagar" field="present_days" right />
                      <SortTh label="Frånv. dagar" field="absent_days" right />
                      <SortTh label="Handläggare" field="handler_name" />
                      <th className="text-left py-2 font-semibold">E-post</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map(p => (
                      <tr key={p.participant_id} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="py-2 font-medium text-slate-800">{p.participant_name}</td>
                        <td className={`py-2 text-right font-bold ${attendanceRateColor(p.attendance_rate)}`}>
                          {p.attendance_rate != null ? `${p.attendance_rate}%` : '—'}
                        </td>
                        <td className="py-2 text-right">{p.present_days}</td>
                        <td className="py-2 text-right">{p.absent_days}</td>
                        <td className="py-2 text-slate-600">{p.handler_name || '—'}</td>
                        <td className="py-2 text-slate-500 text-xs">{p.handler_email || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

// ── UPP-4 Periodöversikt ──────────────────────────────────────────────────

const PeriodSummaryReport = ({ role, participants, handlers }) => {
  const [filter, setFilter] = useState(defaultFilter());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/reports/period-summary?${buildParams(filter)}`);
      setData(res.data);
    } catch {
      toast.error('Kunde inte hämta periodöversikt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="period-summary-report">
      <ReportFilterBar role={role} participants={participants} handlers={handlers} onFilterChange={setFilter} />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Button onClick={fetchReport} disabled={loading} className="rounded-full font-bold" data-testid="generate-period-summary-btn">
          {loading ? 'Hämtar...' : 'Generera rapport'}
        </Button>
        <ReportExportButtons data={data} reportType="period-summary" period={{ from: filter.from_date, to: filter.to_date }} role={role} />
      </div>
      {!data && !loading && (
        <Card><CardContent className="p-10 text-center">
          <BarChart2 size={40} className="mx-auto mb-3 text-muted-foreground" />
          <p className="text-muted-foreground">Klicka "Generera rapport" för att hämta data</p>
        </CardContent></Card>
      )}
      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard label="Aktiva deltagare" value={data.participant_count} />
            <KpiCard label="Inskrivna" value={data.enrolled_in_period} />
            <KpiCard label="Avslutade" value={data.closed_in_period} />
            <KpiCard label="Närvaro" value={data.attendance_rate != null ? `${data.attendance_rate}%` : '—'} />
          </div>
          {data.goals_summary && (
            <Card>
              <CardHeader className="border-b pb-3">
                <CardTitle className="font-heading font-bold text-base">Mål</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="grid grid-cols-4 gap-4">
                  <KpiCard label="Aktiva mål" value={data.goals_summary.active} />
                  <KpiCard label="Uppnådda mål" value={data.goals_summary.completed} />
                  <KpiCard label="Ej påbörjade" value={data.goals_summary.not_started} />
                  <KpiCard label="Uppfyllelsegrad" value={data.goals_summary.achievement_rate != null ? `${data.goals_summary.achievement_rate}%` : '—'} />
                </div>
              </CardContent>
            </Card>
          )}
          {data.deviations_summary && (
            <Card>
              <CardHeader className="border-b pb-3">
                <CardTitle className="font-heading font-bold text-base">Avvikelser</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="grid grid-cols-4 gap-4">
                  <KpiCard label="Totalt" value={data.deviations_summary.total} />
                  <KpiCard label="Öppna" value={data.deviations_summary.open} />
                  <KpiCard label="Åtgärdade" value={data.deviations_summary.resolved} />
                  <KpiCard label="Snitt-åtgärdstid" value={data.deviations_summary.avg_resolution_days != null ? `${data.deviations_summary.avg_resolution_days} dagar` : '—'} />
                </div>
              </CardContent>
            </Card>
          )}
          {data.handler_contacts?.length > 0 && (
            <Card>
              <CardHeader className="border-b pb-3">
                <CardTitle className="font-heading font-bold text-base">Handläggarkontakter</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <table className="w-full text-sm" data-testid="period-summary-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 font-semibold">Handläggare</th>
                      <th className="text-left py-2 font-semibold">E-post</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.handler_contacts.map((h, i) => (
                      <tr key={i} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="py-2 font-medium text-slate-800">{h.name}</td>
                        <td className="py-2 text-slate-500">{h.email}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};


// ── page ───────────────────────────────────────────────────────────────────

const ReportsPage = () => {
  const auth = useContext(AuthContext);
  const role = auth?.user?.role || 'employee';
  const isCustomerAdmin = role === 'customer_admin' || role === 'super_admin';
  const isAssigner = role === 'read_only_assigner';
  const isParticipant = role === 'read_only_participant';

  const canSeeOrgReports = !isParticipant && !isAssigner;
  const canSeeHandlerReport = isCustomerAdmin;
  const canSeeUppReports = !isParticipant;

  const [participants, setParticipants] = useState([]);
  const [handlers, setHandlers] = useState([]);
  const [activeTab, setActiveTab] = useState(() => isAssigner ? 'result' : 'overview');

  useEffect(() => {
    api.get('/participants').then(res => {
      const list = Array.isArray(res.data)
        ? res.data
        : (res.data?.participants || res.data?.items || []);
      setParticipants(list.map(p => ({ id: p.id, name: p.name })));
    }).catch(() => {});

    if (isCustomerAdmin) {
      api.get('/users').then(res => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.users || []);
        setHandlers(
          list
            .filter(u => u.role === 'employee' || u.role === 'customer_admin')
            .map(u => ({ id: u.id, name: u.name }))
        );
      }).catch(() => {});
    }
  }, [isCustomerAdmin]);

  if (isParticipant) {
    return (
      <DashboardLayout>
        <div className="space-y-6" data-testid="reports-page">
          <div>
            <h1 className="text-4xl font-heading font-black tracking-tight">Rapporter</h1>
          </div>
          <Card data-testid="participant-no-reports">
            <CardContent className="p-10 text-center">
              <FileText size={40} className="mx-auto mb-3 text-muted-foreground" />
              <p className="text-muted-foreground">
                Dina rapporter och uppföljningar finns i din profilvy.
              </p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="reports-page">
        <div>
          <h1 className="text-4xl font-heading font-black tracking-tight">Rapporter</h1>
          <p className="text-muted-foreground mt-2">Generera och exportera rapporter</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4 flex-wrap h-auto gap-1">
            {canSeeOrgReports && <TabsTrigger value="overview" data-testid="tab-overview">Översikt</TabsTrigger>}
            {canSeeOrgReports && <TabsTrigger value="attendance" data-testid="tab-attendance">Närvaro</TabsTrigger>}
            {canSeeOrgReports && <TabsTrigger value="goals" data-testid="tab-goals">Mål</TabsTrigger>}
            {canSeeOrgReports && <TabsTrigger value="deviations" data-testid="tab-deviations">Avvikelser</TabsTrigger>}
            {canSeeHandlerReport && <TabsTrigger value="handlers" data-testid="tab-handlers">Handläggare</TabsTrigger>}
            {canSeeOrgReports && <TabsTrigger value="flow" data-testid="tab-flow">Deltagarflöde</TabsTrigger>}
            {canSeeUppReports && <TabsTrigger value="result" data-testid="tab-result">Resultat</TabsTrigger>}
            {canSeeUppReports && <TabsTrigger value="attendance-report" data-testid="tab-attendance-report">Närvarorapport</TabsTrigger>}
            {canSeeUppReports && <TabsTrigger value="period" data-testid="tab-period">Period</TabsTrigger>}
            {canSeeOrgReports && <TabsTrigger value="deltagarrapport" data-testid="tab-deltagarrapport">Deltagarrapport</TabsTrigger>}
          </TabsList>

          {canSeeOrgReports && (
            <TabsContent value="overview">
              <OverviewReport role={role} participants={participants} handlers={handlers} />
            </TabsContent>
          )}
          {canSeeOrgReports && (
            <TabsContent value="attendance">
              <AttendanceTrendReport role={role} participants={participants} handlers={handlers} />
            </TabsContent>
          )}
          {canSeeOrgReports && (
            <TabsContent value="goals">
              <GoalSummaryReport role={role} participants={participants} handlers={handlers} />
            </TabsContent>
          )}
          {canSeeOrgReports && (
            <TabsContent value="deviations">
              <DeviationsReport />
            </TabsContent>
          )}
          {canSeeHandlerReport && (
            <TabsContent value="handlers">
              <HandlerOverviewReport role={role} participants={participants} handlers={handlers} />
            </TabsContent>
          )}
          {canSeeOrgReports && (
            <TabsContent value="flow">
              <ParticipantFlowReport role={role} participants={participants} handlers={handlers} />
            </TabsContent>
          )}
          {canSeeUppReports && (
            <TabsContent value="result">
              <ResultSummaryReport role={role} participants={participants} handlers={handlers} />
            </TabsContent>
          )}
          {canSeeUppReports && (
            <TabsContent value="attendance-report">
              <AttendanceReport role={role} participants={participants} handlers={handlers} />
            </TabsContent>
          )}
          {canSeeUppReports && (
            <TabsContent value="period">
              <PeriodSummaryReport role={role} participants={participants} handlers={handlers} />
            </TabsContent>
          )}
          {canSeeOrgReports && (
            <TabsContent value="deltagarrapport">
              <ExistingReports />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default ReportsPage;
